<div class="flex flex-col">
  <div *ngIf="!managingLocation" class="md:mt-2">
    <div class="flex justify-between gap-5">
      <button *ngIf="entityId && entityType && !isLoading && canEdit" type="button" mat-button color="primary"
              [disabled]="isSaving" (click)="addLocation()">
        Add a Service Location
      </button>
      <button *ngIf="entityId && entityType && !isLoading" type="button" mat-button color="primary" class="ml-auto"
              (click)="loadLocations(!includeArchived)">
        {{ includeArchived ? 'Hide Archived' : 'Show Archived' }}
      </button>
    </div>

    <mat-table [dataSource]="dataSource" matSort aria-label="Hospital service locations"
               class="portal-table">
      <ng-container matColumnDef="floor">
        <mat-header-cell *matHeaderCellDef mat-sort-header>Floor</mat-header-cell>
        <mat-cell *matCellDef="let serviceLocation">
          <span class="mobile-label">Floor</span>
          {{ serviceLocation.floor || 'Unnamed service location' }}
          <span *ngIf="serviceLocation.isDraft" class="ml-2">Draft — incomplete</span>
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="suite">
        <mat-header-cell *matHeaderCellDef mat-sort-header>Suite</mat-header-cell>
        <mat-cell *matCellDef="let serviceLocation">
          <span class="mobile-label">Suite</span>
          {{ serviceLocation.suite }}
        </mat-cell>
      </ng-container>

      <ng-container *ngIf="showType" matColumnDef="type">
        <mat-header-cell *matHeaderCellDef mat-sort-header>Type</mat-header-cell>
        <mat-cell *matCellDef="let serviceLocation">
          <span class="mobile-label">Type</span>
          <div class="flex flex-col">
            <span *ngFor="let type of parseTypes(serviceLocation.type)">{{ type }}</span>
          </div>
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="servicesProvided">
        <mat-header-cell *matHeaderCellDef mat-sort-header>Services Provided</mat-header-cell>
        <mat-cell *matCellDef="let serviceLocation">
          <span class="mobile-label">Services Provided</span>
          {{ serviceLocation.servicesProvided | truncate:40 }}
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="archived">
        <mat-header-cell *matHeaderCellDef></mat-header-cell>
        <mat-cell *matCellDef="let serviceLocation">
          <span class="font-semibold" *ngIf="serviceLocation.archived">Archived</span>
        </mat-cell>
      </ng-container>

      <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
      <mat-row *matRowDef="let row; columns: displayedColumns" tabindex="0"
               class="cursor-pointer portal-interactive-row"
               [ngClass]="row.archived ? 'portal-archived' : ''"
               (click)="canEdit? editLocation(row) : null" (keydown.enter)="canEdit? editLocation(row) : null ">
      </mat-row>
    </mat-table>

    <mat-paginator [pageSizeOptions]="[5, 10, 20]" showFirstLastButtons></mat-paginator>

    <div *ngIf="isLoading" class="p-4 text-center">Loading service locations...</div>
    <div *ngIf="!isLoading && dataSource.data.length === 0" class="p-4 text-center">
      No service locations found.
    </div>
  </div>

  <div *ngIf="managingLocation" class="flex flex-col w-full pt-4" [attr.inert]="isSaving ? '' : null" [@slideRight]>
    <mat-card>
      <mat-card-content>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col md:flex-row justify-between md:items-center gap-2">
            <span class="font-semibold">
              {{ location.id === 0 ? 'Add a Service Location' : 'Edit Service Location' }}
            </span>
            <div class="flex items-center gap-2">
              <button type="button" mat-button color="primary"
                      [disabled]="isSaving" (click)="saveLocation()">
                Save
              </button>
              <button type="button" mat-button color="warn" [disabled]="isSaving" (click)="closeWithSave()">
                Close
              </button>
              <button *ngIf="location.id > 0" type="button" mat-button color="warn"
                      [disabled]="isSaving" (click)="deleteLocation(location)">
                Delete
              </button>
              <button *ngIf="location.id > 0" type="button" mat-icon-button
                      [color]="location.archived ? 'primary' : 'warn'"
                      [disabled]="isSaving"
                      (click)="archiveLocation(location, !location.archived)"
                      [matTooltip]="location.archived ? 'Un-archive Location' : 'Archive Location'">
                <mat-icon [fontIcon]="location.archived ? 'unarchive' : 'archive'"></mat-icon>
              </button>
            </div>
          </div>

          <p role="status" aria-live="polite">{{ saver?.status }} <span *ngIf="location.isDraft">Draft — complete required details and all days of office hours before submitting.</span></p>
          <form [formGroup]="locationForm">
            <div class="flex flex-col">
              <div class="flex flex-col md:flex-row md:gap-4">
                <mat-form-field class="flex md:w-1/2">
                  <mat-label>Floor</mat-label>
                  <input matInput formControlName="floor">
                  <mat-error *ngIf="locationForm.get('floor')?.hasError('required')">Floor is required</mat-error>
                </mat-form-field>

                <mat-form-field class="flex md:w-1/2">
                  <mat-label>Suite</mat-label>
                  <input matInput formControlName="suite">
                  <mat-error *ngIf="locationForm.get('suite')?.hasError('required')">Suite is required</mat-error>
                </mat-form-field>
              </div>

              <div class="flex flex-col md:flex-row md:gap-4">
                <div *ngIf="showType" class="flex flex-col w-1/2 gap-2">
                  <span>Type (Select all that apply)</span>
                  <div class="flex flex-col gap-2 p-2">
                    <mat-checkbox formControlName="typeOutpatient">
                      <span>Outpatient Satellite</span>
                    </mat-checkbox>
                    <mat-checkbox formControlName="typePsychiatric">
                      <span>Psychiatric Satellite</span>
                    </mat-checkbox>
                    <mat-checkbox formControlName="typeEmergency">
                      <span>Emergency Medical Services Satellite (also known as "Off-campus Emergency Departments")</span>
                    </mat-checkbox>
                    <mat-error *ngIf="locationForm.hasError('noTypeSelected') &&
                                      (locationForm.get('typeOutpatient')?.touched ||
                                       locationForm.get('typePsychiatric')?.touched ||
                                       locationForm.get('typeEmergency')?.touched)" class="portal-error text-sm">
                      At least one type must be selected
                    </mat-error>
                  </div>
                </div>

                <mat-form-field [ngClass]="showType? 'flex md:w-1/2' : 'w-full'">
                  <mat-label>Services Provided</mat-label>
                  <textarea matInput formControlName="servicesProvided" rows="4"></textarea>
                  <mat-error *ngIf="locationForm.get('servicesProvided')?.hasError('required')">
                    Services provided is required
                  </mat-error>
                </mat-form-field>
              </div>
            </div>
          </form>

          <app-office-hours [entityId]="location.id"
                            [entityType]="entity"
                            [canEdit]="canEdit && !location.archived" [managed]="true" [managedHours]="initialHours"
                            (hoursChange)="hoursChanged($event)">
          </app-office-hours>

        </div>
      </mat-card-content>
    </mat-card>
  </div>
</div>
